import type { CalendarDay } from '../validators/temporal.validator.js'

/**
 * A venue's package prices (docs/sales.spec.md, "The price list"): each
 * package is priced by season, the date ranges the venue names, and by day of
 * the week, with "any other date" for the days no season covers.
 */

export interface SeasonRange {
  id: string
  name: string
  /** Inclusive. */
  from: CalendarDay
  to: CalendarDay
}

export interface PackagePriceRow {
  /** Null: any date outside the venue's seasons. */
  seasonId: string | null
  /** 0 is Sunday, 6 Saturday. */
  weekdays: readonly number[]
  priceCents: number
}

export const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const

/** The day of the week of a calendar day, 0 for Sunday. */
export function weekdayOf(day: CalendarDay): number {
  return new Date(`${day}T00:00:00.000Z`).getUTCDay()
}

/** The season a day falls in, if any. Seasons never overlap, so there is at most one. */
function seasonOf(seasons: readonly SeasonRange[], day: CalendarDay): SeasonRange | null {
  return seasons.find((s) => s.from <= day && day <= s.to) ?? null
}

/**
 * A package's price on a day: its row for the day's season and weekday, else
 * its "any other date" row for the weekday. Null when it has no price that day,
 * so staff type one or leave the package out.
 */
export function packagePrice(rows: readonly PackagePriceRow[], seasons: readonly SeasonRange[], day: CalendarDay): number | null {
  const weekday = weekdayOf(day)
  const season = seasonOf(seasons, day)
  const row =
    (season === null ? undefined : rows.find((r) => r.seasonId === season.id && r.weekdays.includes(weekday))) ??
    rows.find((r) => r.seasonId === null && r.weekdays.includes(weekday))
  return row?.priceCents ?? null
}

/** Why a venue's seasons can't be saved, in plain words; empty when they can. */
export function seasonErrors(seasons: readonly SeasonRange[]): string[] {
  const errors: string[] = []
  for (const s of seasons) if (s.to < s.from) errors.push(`${s.name} ends before it starts.`)
  const sorted = [...seasons].sort((a, b) => (a.from < b.from ? -1 : a.from > b.from ? 1 : 0))
  for (let i = 1; i < sorted.length; i++) {
    const before = sorted[i - 1]
    const after = sorted[i]
    if (before !== undefined && after !== undefined && after.from <= before.to) errors.push(`${before.name} and ${after.name} overlap.`)
  }
  return errors
}

/** Why a package's prices can't be saved; `seasonName` names a row's season for the message. */
export function packagePriceErrors(
  packageName: string,
  rows: readonly PackagePriceRow[],
  seasonName: (seasonId: string | null) => string,
): string[] {
  const errors: string[] = []
  if (rows.length === 0) errors.push(`${packageName} needs a price.`)
  const seen = new Map<string, Set<number>>()
  for (const row of rows) {
    if (row.weekdays.length === 0) errors.push(`A price for ${packageName} needs at least one day of the week.`)
    const key = row.seasonId ?? ''
    const days = seen.get(key) ?? new Set<number>()
    for (const day of row.weekdays) {
      const name = WEEKDAY_NAMES[day]
      if (name === undefined) continue
      if (days.has(day)) errors.push(`${packageName} has two prices for ${name} in ${seasonName(row.seasonId)}.`)
      days.add(day)
    }
    seen.set(key, days)
  }
  return errors
}
